/**
 * ============================================================
 * DNINE UNIFIED FORM HANDLER
 * ============================================================
 *
 * Centralized form submission handler for all DNINE website forms:
 * - Contact Us
 * - Become a Reseller
 * - Careers / Job Applications
 *
 * Note: Warranty form has its own dedicated handler (warranty-form-v2.js)
 *
 * Features:
 * - Google Sheets integration
 * - File uploads (CV, cover letters)
 * - Shopify customer data integration
 * - Professional validation
 * - Loading states and error handling
 *
 * ============================================================
 */

class DNINEFormHandler {
  constructor(config = {}) {
    this.config = {
      // Google Apps Script URL - MUST BE SET
      googleScriptUrl: config.googleScriptUrl || 'YOUR_GOOGLE_APPS_SCRIPT_URL_HERE',

      // Form type identifier
      formType: config.formType || 'unknown',

      // Form element ID
      formId: config.formId || null,

      // Success callback
      onSuccess: config.onSuccess || null,

      // Error callback
      onError: config.onError || null,

      // Auto-fill from Shopify customer
      autoFillCustomer: config.autoFillCustomer !== false,

      // File upload fields (array of field IDs)
      fileFields: config.fileFields || [],

      // Max file size in bytes (default 5MB)
      maxFileSize: config.maxFileSize || 5 * 1024 * 1024,

      // Allowed file types
      allowedFileTypes: config.allowedFileTypes || ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
    };

    this.form = null;
    this.submitButton = null;
    this.isSubmitting = false;
    this.uploadedFiles = {};
    this.customer = window.Shopify?.customer || null;

    if (this.config.formId) {
      this.init();
    }
  }

  init() {
    this.form = document.getElementById(this.config.formId);

    if (!this.form) {
      console.warn(`DNINE Form Handler: Form with ID "${this.config.formId}" not found`);
      return;
    }

    this.submitButton = this.form.querySelector('button[type="submit"]');

    // Auto-fill customer data if enabled
    if (this.config.autoFillCustomer && this.customer) {
      this.autoFillCustomerData();
    }

    // Setup file upload handlers
    this.setupFileUploads();

    // Setup form submission
    this.setupFormSubmit();

    console.log(`DNINE Form Handler: Initialized for ${this.config.formType} form`);
  }

  /**
   * Auto-fill customer data from Shopify customer object
   */
  autoFillCustomerData() {
    if (!this.customer) return;

    // Common field mappings
    const fieldMappings = {
      'name': this.customer.name,
      'email': this.customer.email,
      'phone': this.customer.phone || this.customer.default_address?.phone
    };

    Object.entries(fieldMappings).forEach(([fieldName, value]) => {
      if (!value) return;

      // Try multiple ID patterns
      const patterns = [
        `${this.config.formType}-${fieldName}`,
        `ContactForm-${fieldName}`,
        fieldName
      ];

      for (const pattern of patterns) {
        const field = this.form.querySelector(`#${pattern}`) ||
                     this.form.querySelector(`[name*="${fieldName}"]`);
        if (field && !field.value) {
          field.value = value;
          console.log(`DNINE Form Handler: Auto-filled ${fieldName}`);
          break;
        }
      }
    });
  }

  /**
   * Setup file upload handlers
   */
  setupFileUploads() {
    this.config.fileFields.forEach(fieldId => {
      const fileInput = document.getElementById(fieldId);
      if (!fileInput) return;

      fileInput.addEventListener('change', (e) => {
        this.handleFileSelect(fieldId, e.target.files[0]);
      });
    });
  }

  /**
   * Handle file selection
   */
  handleFileSelect(fieldId, file) {
    if (!file) {
      delete this.uploadedFiles[fieldId];
      this.updateFileDisplay(fieldId, null);
      return;
    }

    // Validate file
    if (file.size > this.config.maxFileSize) {
      alert(`File is too large. Maximum size is ${this.formatFileSize(this.config.maxFileSize)}`);
      this.clearFileInput(fieldId);
      return;
    }

    if (!this.config.allowedFileTypes.includes(file.type)) {
      alert('Invalid file type. Please upload PDF, JPG, or PNG only.');
      this.clearFileInput(fieldId);
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      this.uploadedFiles[fieldId] = {
        name: file.name,
        type: file.type,
        size: file.size,
        data: e.target.result.split(',')[1] // Remove data:...;base64, prefix
      };
      this.updateFileDisplay(fieldId, file.name);
      console.log(`DNINE Form Handler: File ready for ${fieldId}`);
    };
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
      this.clearFileInput(fieldId);
    };
    reader.readAsDataURL(file);
  }

  /**
   * Clear file input
   */
  clearFileInput(fieldId) {
    const input = document.getElementById(fieldId);
    if (input) input.value = '';
    delete this.uploadedFiles[fieldId];
    this.updateFileDisplay(fieldId, null);
  }

  /**
   * Update file display element
   */
  updateFileDisplay(fieldId, fileName) {
    const displayId = `${fieldId}-display`;
    const display = document.getElementById(displayId);

    if (display) {
      if (fileName) {
        display.textContent = fileName;
        display.classList.add('has-file');
      } else {
        display.textContent = 'No file chosen';
        display.classList.remove('has-file');
      }
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /**
   * Setup form submission handler
   */
  setupFormSubmit() {
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (this.isSubmitting) {
        console.log('DNINE Form Handler: Already submitting');
        return;
      }

      // Validate form
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      await this.submitForm();
    });
  }

  /**
   * Submit form data
   */
  async submitForm() {
    this.isSubmitting = true;
    this.setLoadingState(true);

    try {
      // Collect form data
      const formData = this.collectFormData();

      console.log(`DNINE Form Handler: Submitting ${this.config.formType} form`, formData);

      // Send to Google Sheets
      const response = await this.sendToGoogleSheets(formData);

      if (response.success) {
        this.handleSuccess(formData);
      } else {
        throw new Error(response.message || 'Submission failed');
      }

    } catch (error) {
      console.error('DNINE Form Handler: Error:', error);
      this.handleError(error);
    } finally {
      this.isSubmitting = false;
      this.setLoadingState(false);
    }
  }

  /**
   * Collect form data based on form type
   */
  collectFormData() {
    const formDataObj = new FormData(this.form);
    const data = {
      formType: this.config.formType,
      timestamp: new Date().toISOString(),
      source: 'Website'
    };

    // Convert FormData to object
    for (const [key, value] of formDataObj.entries()) {
      // Clean up key names (remove contact[] wrapper from Shopify forms)
      let cleanKey = key.replace(/^contact\[|\]$/g, '');
      cleanKey = this.toCamelCase(cleanKey);
      data[cleanKey] = value;
    }

    // Add uploaded files
    Object.entries(this.uploadedFiles).forEach(([fieldId, fileData]) => {
      data[this.toCamelCase(fieldId) + 'File'] = fileData;
    });

    // Add Shopify customer data if available
    if (this.customer) {
      data.shopifyCustomerId = this.customer.id;
      data.shopifyCustomerEmail = this.customer.email;
    }

    return data;
  }

  /**
   * Convert string to camelCase
   */
  toCamelCase(str) {
    return str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
      .replace(/^[A-Z]/, chr => chr.toLowerCase());
  }

  /**
   * Send data to Google Sheets
   */
  async sendToGoogleSheets(formData) {
    if (this.config.googleScriptUrl === 'YOUR_GOOGLE_APPS_SCRIPT_URL_HERE') {
      console.warn('DNINE Form Handler: Google Script URL not configured');

      // Simulate success for testing
      return new Promise(resolve => {
        setTimeout(() => {
          resolve({ success: true, message: 'Test mode - URL not configured' });
        }, 1500);
      });
    }

    try {
      const response = await fetch(this.config.googleScriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      // With no-cors, we can't read response, assume success
      return { success: true };

    } catch (error) {
      console.error('DNINE Form Handler: Fetch error:', error);
      throw error;
    }
  }

  /**
   * Handle successful submission
   */
  handleSuccess(formData) {
    console.log(`DNINE Form Handler: ${this.config.formType} form submitted successfully`);

    // Reset form
    this.form.reset();
    this.uploadedFiles = {};

    // Clear file displays
    this.config.fileFields.forEach(fieldId => {
      this.updateFileDisplay(fieldId, null);
    });

    // Call success callback if provided
    if (typeof this.config.onSuccess === 'function') {
      this.config.onSuccess(formData);
    } else {
      // Default success handling - show alert or success message
      this.showDefaultSuccess();
    }

    // Track analytics
    this.trackEvent(`${this.config.formType}_form_success`, formData);
  }

  /**
   * Handle submission error
   */
  handleError(error) {
    console.error(`DNINE Form Handler: ${this.config.formType} form error:`, error);

    if (typeof this.config.onError === 'function') {
      this.config.onError(error);
    } else {
      // Default error handling
      this.showDefaultError(error.message);
    }

    // Track analytics
    this.trackEvent(`${this.config.formType}_form_error`, { error: error.message });
  }

  /**
   * Show default success message
   */
  showDefaultSuccess() {
    // Try to find success message element
    const successEl = document.getElementById(`${this.config.formType}-success`) ||
                     document.querySelector('.form-success-message');

    if (successEl) {
      successEl.style.display = 'block';
      successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      alert('Form submitted successfully! We will get back to you soon.');
    }
  }

  /**
   * Show default error message
   */
  showDefaultError(message) {
    const errorEl = document.getElementById(`${this.config.formType}-error`) ||
                   document.querySelector('.form-error-message');

    if (errorEl) {
      const textEl = errorEl.querySelector('.message-text') || errorEl;
      textEl.textContent = message || 'An error occurred. Please try again.';
      errorEl.style.display = 'block';
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      alert(message || 'An error occurred. Please try again.');
    }
  }

  /**
   * Set loading state on submit button
   */
  setLoadingState(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.disabled = true;
      this.submitButton.dataset.originalText = this.submitButton.textContent;
      this.submitButton.textContent = 'Submitting...';
      this.submitButton.classList.add('loading');
    } else {
      this.submitButton.disabled = false;
      if (this.submitButton.dataset.originalText) {
        this.submitButton.textContent = this.submitButton.dataset.originalText;
      }
      this.submitButton.classList.remove('loading');
    }
  }

  /**
   * Track analytics events
   */
  trackEvent(eventName, eventData = {}) {
    // Google Analytics 4
    if (typeof gtag === 'function') {
      gtag('event', eventName, eventData);
    }

    // Microsoft Clarity
    if (typeof clarity === 'function') {
      clarity('event', eventName);
    }

    console.log(`DNINE Form Handler: Event - ${eventName}`, eventData);
  }
}

// ============================================================
// AUTO-INITIALIZE FOR COMMON FORMS
// ============================================================

document.addEventListener('DOMContentLoaded', () => {
  // Get Google Script URL from global config or data attribute
  const scriptUrl = window.DNINE_GOOGLE_SCRIPT_URL ||
                   document.body.dataset.googleScriptUrl ||
                   'YOUR_GOOGLE_APPS_SCRIPT_URL_HERE';

  // Initialize Contact Form
  const contactForm = document.getElementById('ContactForm');
  if (contactForm && !contactForm.dataset.dnineInitialized) {
    window.dnineContactForm = new DNINEFormHandler({
      googleScriptUrl: scriptUrl,
      formType: 'contact',
      formId: 'ContactForm',
      onSuccess: () => {
        // Show Shopify's built-in success message if available
        const successMsg = contactForm.querySelector('.form-status');
        if (successMsg) {
          successMsg.style.display = 'block';
        }
      }
    });
    contactForm.dataset.dnineInitialized = 'true';
  }

  // Initialize Reseller Form
  const resellerForm = document.getElementById('ResellerForm') ||
                       document.querySelector('[data-form-type="reseller"]');
  if (resellerForm && !resellerForm.dataset.dnineInitialized) {
    window.dnineResellerForm = new DNINEFormHandler({
      googleScriptUrl: scriptUrl,
      formType: 'reseller',
      formId: resellerForm.id || 'ContactForm',
      onSuccess: () => {
        alert('Thank you for your interest! Our team will contact you within 3-5 business days.');
      }
    });
    resellerForm.dataset.dnineInitialized = 'true';
  }

  // Initialize Careers Form
  const careersForm = document.getElementById('CareersForm') ||
                      document.querySelector('[data-form-type="careers"]');
  if (careersForm && !careersForm.dataset.dnineInitialized) {
    window.dnineCareersForm = new DNINEFormHandler({
      googleScriptUrl: scriptUrl,
      formType: 'careers',
      formId: careersForm.id,
      fileFields: ['cv-upload', 'cover-letter-upload'],
      onSuccess: () => {
        alert('Thank you for applying! We will review your application and get back to you soon.');
      }
    });
    careersForm.dataset.dnineInitialized = 'true';
  }

  console.log('DNINE Form Handler: Auto-initialization complete');
});

// Add CSS for loading state
const style = document.createElement('style');
style.textContent = `
  button.loading {
    opacity: 0.7;
    cursor: not-allowed;
    position: relative;
  }

  button.loading::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 15px;
    width: 16px;
    height: 16px;
    margin-top: -8px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: dnine-spinner 0.6s linear infinite;
  }

  @keyframes dnine-spinner {
    to { transform: rotate(360deg); }
  }

  .file-name.has-file {
    color: #065f46;
    background: #d1fae5;
    border-color: #10b981;
    font-weight: 600;
  }
`;
document.head.appendChild(style);

// Export for use in other scripts
window.DNINEFormHandler = DNINEFormHandler;

console.log('DNINE Form Handler: Script loaded');
